import { ChangeDetectionStrategy, Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IStorage, LocalStorage } from '@isophi/core-legacy';
import { IconService, targetStateData } from '@isophi/core-ui';
import { ManagerTeacher, StateEnum, TeachersService } from '@isophi/mng-api';
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, map, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';

import { HelperService } from '../../../../core/services/helper.service';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { QuickRangeKey } from '../../../children-portfolio/constants/types';
import { PlanFilterForm, PlanFilterFormValue } from '../../constants/forms';
import { PlanFilterCriteria } from '../../constants/models';

@Component({
  selector: 'app-pap-plan-filter',
  templateUrl: './pap-plan-filter.component.html',
  styleUrls: ['./pap-plan-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapPlanFilterComponent extends BaseComponent implements OnInit {
  @Output() filterUpdated = new EventEmitter<PlanFilterCriteria>();

  @Output() export = new EventEmitter<'xlsx' | 'csv'>();
  @Output() print = new EventEmitter<void>();

  private readonly lSConstService = inject(LocalStorageConstService);
  private readonly helperService = inject(HelperService);
  private readonly teacherService = inject(TeachersService);
  iconService = inject(IconService);
  private storage: IStorage = new LocalStorage();

  targetStates = this.helperService.prepareData(targetStateData, StateEnum);

  hoveredDate: NgbDate | null = null;

  fetchTeachers$ = new Subject<string>();

  teachers$: Observable<ManagerTeacher[]> = this.fetchTeachers$.pipe(
    startWith(undefined),
    switchMap((search) => this.teacherService.teachersList(undefined, undefined, undefined, undefined, search)),
    takeUntil(this.ngUnsubscribe$),
    map((teachers) =>
      teachers.results.map((teacher) => ({
        ...teacher,
        fullName: `${teacher.firstName} ${teacher.lastName}`,
      }))
    )
  );

  filterForm = new FormGroup<PlanFilterForm>({
    search: new FormControl<string | null>(null),
    teacherId: new FormControl<number | null>(null),
    state: new FormControl<string | null>(null),
    dateQuickRangeKey: new FormControl<QuickRangeKey | null>(null),
    dateStart: new FormControl<NgbDateStruct>(null),
    dateEnd: new FormControl<NgbDateStruct>(null),
  });

  ngOnInit(): void {
    this.filterForm.valueChanges.pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$)).subscribe((filterFormValue) => {
      this.filterPortfolioActivities(filterFormValue as PlanFilterFormValue);
    });
    if (this.storage.hasItem(this.lSConstService.papPlanFilter)) {
      const storageData = this.storage.getItem(this.lSConstService.papPlanFilter);
      if (storageData && storageData !== 'undefined') {
        const filterCriteria: PlanFilterCriteria = JSON.parse(storageData);
        this.filterForm.patchValue({
          search: '',
          teacherId: filterCriteria.teacherId,
          state: filterCriteria.state,
          dateQuickRangeKey: filterCriteria.dateQuickRangeKey,
          dateStart: this.helperService.parseDate(filterCriteria.dateStart),
          dateEnd: this.helperService.parseDate(filterCriteria.dateEnd),
        });
      }
    }
  }

  exportPage(): void {
    this.export.emit('xlsx');
  }

  printPage(): void {
    this.print.emit();
  }

  filterPortfolioActivities(filterFormValue: PlanFilterFormValue): void {
    const filterCriteria: PlanFilterCriteria = {
      search: filterFormValue.search,
      teacherId: filterFormValue.teacherId,
      state: filterFormValue.state,
      dateQuickRangeKey: filterFormValue.dateQuickRangeKey,
      dateStart: this.helperService.formateDate(filterFormValue.dateStart),
      dateEnd: this.helperService.formateDate(filterFormValue.dateEnd),
    };
    this.storage.setItem(this.lSConstService.papPlanFilter, JSON.stringify(filterCriteria));
    this.filterUpdated.emit(filterCriteria);
  }
}
